import FeatureCard from "./FeatureCard";
import { Mail, FileText, Folder, PaperclipIcon, Home, Users, Briefcase, Phone, Menu, X, Code, Rocket, Settings } from 'lucide-react';
// Services Page
const ServicesPage = () => {
  return (
    <div>
      {/* Hero Section */}
      <section id="services" className="bg-amber-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl font-bold text-gray-800 mb-6">Our Services</h1>
            <p className="text-xl text-gray-600">
              Comprehensive solutions for your tech talent and software development needs.
            </p>
          </div>
        </div>
      </section>

      {/* Recruitment Services */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Tech Recruitment Services</h2>
            <div className="bg-white p-8 rounded-lg shadow-md mb-8">
              <div className="flex items-start mb-6">
                <div className="bg-amber-100 p-3 rounded-full mr-4">
                  <Users className="text-gray-800" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Technical Talent Acquisition</h3>
                  <p className="text-gray-600">
                    We help you find the perfect candidates for your technical roles, from developers and engineers to IT managers and CTOs. Our thorough vetting process ensures you only meet candidates who match your requirements.
                  </p>
                </div>
              </div>

              <div className="flex items-start mb-6">
                <div className="bg-amber-100 p-3 rounded-full mr-4">
                  <Briefcase className="text-gray-800" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Contract & Permanent Staffing</h3>
                  <p className="text-gray-600">
                    Whether you need talent for a short-term project or a permanent role, we have the network and expertise to fulfill your staffing needs quickly and efficiently.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-amber-100 p-3 rounded-full mr-4">
                  <FileText className="text-gray-800" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Executive Search</h3>
                  <p className="text-gray-600">
                    For senior and executive tech positions, our specialized search process helps you identify and attract top-tier leadership talent that can drive your organization forward.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Software Development Services */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Software Development Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <FeatureCard
                icon={<FileText className="text-gray-800" />}
                title="Custom Application Development"
                description="Bespoke software solutions tailored to your specific business needs and objectives."
                className="h-full"
              />
              <FeatureCard
                icon={<Folder className="text-gray-800" />}
                title="Web & Mobile Development"
                description="Responsive web applications and mobile apps that provide seamless user experiences."
                className="h-full"
              />
              <FeatureCard
                icon={<Mail className="text-gray-800" />}
                title="Enterprise Solutions"
                description="Scalable, secure, and robust solutions for large organizations with complex requirements."
                className="h-full"
              />
              <FeatureCard
                icon={<PaperclipIcon className="text-gray-800" />}
                title="UI/UX Design"
                description="User-centered design that enhances usability and creates intuitive interfaces."
                className="h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">Our Process</h2>
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-amber-200"></div>

              {/* Timeline items */}
              <div className="space-y-12">
                <div className="flex flex-col md:flex-row items-center">
                  <div className="md:w-1/2 md:pr-8 mb-4 md:mb-0">
                    <div className="bg-white p-6 rounded-lg shadow-md">
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">1. Consultation</h3>
                      <p className="text-gray-600">
                        We begin by understanding your needs, whether it's finding the right talent or developing custom software.
                      </p>
                    </div>
                  </div>
                  <div className="bg-amber-200 rounded-full h-10 w-10 flex items-center justify-center z-10">
                    <span className="text-gray-800 font-bold">1</span>
                  </div>
                  <div className="md:w-1/2 md:pl-8"></div>
                </div>

                <div className="flex flex-col md:flex-row items-center">
                  <div className="md:w-1/2 md:pr-8 order-1 md:order-none"></div>
                  <div className="bg-amber-200 rounded-full h-10 w-10 flex items-center justify-center z-10 order-none">
                    <span className="text-gray-800 font-bold">2</span>
                  </div>
                  <div className="md:w-1/2 md:pl-8 mb-4 md:mb-0 order-none">
                    <div className="bg-white p-6 rounded-lg shadow-md">
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">2. Strategy Development</h3>
                      <p className="text-gray-600">
                        We create a tailored strategy to address your specific challenges and objectives.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col md:flex-row items-center">
                  <div className="md:w-1/2 md:pr-8 mb-4 md:mb-0">
                    <div className="bg-white p-6 rounded-lg shadow-md">
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">3. Implementation</h3>
                      <p className="text-gray-600">
                        Our team executes the plan, whether it's searching for candidates or developing software.
                      </p>
                    </div>
                  </div>
                  <div className="bg-amber-200 rounded-full h-10 w-10 flex items-center justify-center z-10">
                    <span className="text-gray-800 font-bold">3</span>
                  </div>
                  <div className="md:w-1/2 md:pl-8"></div>
                </div>

                <div className="flex flex-col md:flex-row items-center">
                  <div className="md:w-1/2 md:pr-8 order-1 md:order-none"></div>
                  <div className="bg-amber-200 rounded-full h-10 w-10 flex items-center justify-center z-10 order-none">
                    <span className="text-gray-800 font-bold">4</span>
                  </div>
                  <div className="md:w-1/2 md:pl-8 mb-4 md:mb-0 order-none">
                    <div className="bg-white p-6 rounded-lg shadow-md">
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">4. Delivery & Support</h3>
                      <p className="text-gray-600">
                        We deliver results and provide ongoing support to ensure long-term success.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      {/* <section className="bg-amber-50 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">Our Pricing Models</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="bg-gray-800 text-white p-6 text-center">
                <h3 className="text-xl font-semibold mb-2">Recruitment Basic</h3>
                <p className="text-3xl font-bold">15%</p>
                <p className="text-sm">of annual salary</p>
              </div>
              <div className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span>Initial screening</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span>30-day guarantee</span>
                  </li>
                </ul>
                <button className="mt-6 w-full bg-amber-200 text-gray-800 py-2 rounded-md font-medium hover:bg-amber-300 transition-colors">
                  Contact Us
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden transform scale-105">
              <div className="bg-amber-200 text-gray-800 p-6 text-center">
                <h3 className="text-xl font-semibold mb-2">Recruitment Pro</h3>
                <p className="text-3xl font-bold">20%</p>
                <p className="text-sm">of annual salary</p>
              </div>
              <div className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span>All Basic features</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span>Technical assessments</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span>Cultural fit evaluation</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span>60-day guarantee</span>
                  </li>
                </ul>
                <button className="mt-6 w-full bg-gray-800 text-white py-2 rounded-md font-medium hover:bg-gray-700 transition-colors">
                  Contact Us
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="bg-gray-800 text-white p-6 text-center">
                <h3 className="text-xl font-semibold mb-2">Custom Development</h3>
                <p className="text-3xl font-bold">Custom</p>
                <p className="text-sm">project-based pricing</p>
              </div>
              <div className="p-6">
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span>Requirements analysis</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span>Custom development</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span>Quality assurance</span>
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span>Ongoing support</span>
                  </li>
                </ul>
                <button className="mt-6 w-full bg-amber-200 text-gray-800 py-2 rounded-md font-medium hover:bg-amber-300 transition-colors">
                  Request Quote
                </button>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="bg-gray-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Business?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contact us today to discuss how our services can help you achieve your goals.
          </p>
          <button className="bg-amber-200 text-gray-800 px-8 py-3 rounded-md font-medium hover:bg-amber-300 transition-colors">
            Get Started
          </button>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage